import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { usePaymentLink } from '@/hooks/usePaymentLink';
import { getErrorMessage } from '@/helpers/errorMessages';
import { findReservationForProductDates } from '@/helpers/payReservationMatch';
import {
    getHospitalityReservationByToken,
    getHospitalityReservationList,
} from '@/services/hospitality.service';
import { useToken } from '@/store/auth/tokenStore';
import { useModalAuthStore } from '@/store/auth/authModalStore';
import {
    CartServiceType,
    useShoppingCartStore,
} from '@/store/shoppingCart/shoppingCartStore';
import { useReservationStore } from '@/store/reservation/reservationStore';
import type { PreviewProduct } from '../Preview';

export type PayReservationState =
    | { status: 'loading' }
    | { status: 'notFound' }
    | { status: 'error'; message: string }
    | { status: 'preview'; forcedCheckoutId: number }
    | { status: 'checkout'; forcedCheckoutId: number };

export const usePayReservationLogic = (): {
    state: PayReservationState;
    openLogin: () => void;
} => {
    const { paymentLinkId } = useParams();
    const navigate = useNavigate();
    const { token } = useToken();
    const { setIsVisibleAuthModal } = useModalAuthStore();
    const { setCartValues, cartValues, setIsPaymentLinkCart, cleanCart } =
        useShoppingCartStore();
    const { setActiveReservation } = useReservationStore();

    const paymentLink = usePaymentLink(paymentLinkId);

    const products: PreviewProduct[] = useMemo(() => {
        if (!paymentLink.data) return [];
        return paymentLink.data.products.map(item => ({
            id: item.id,
            title: item.product.exhibitionName,
            date: new Date(item.date),
            schedule: item.schedule?.name ?? '',
            surfer: item.client?.name ?? '',
        }));
    }, [paymentLink.data]);

    const resolution = useQuery({
        queryKey: ['payReservationResolve', paymentLinkId],
        enabled: Boolean(token) && Boolean(paymentLink.data),
        queryFn: async () => {
            const { items, error } = await getHospitalityReservationList();
            if (error) throw new Error(error);

            const match = findReservationForProductDates(
                products.map(product => product.date),
                items ?? []
            );
            if (!match) return { match: null };

            const detail = await getHospitalityReservationByToken(match.token);
            if (detail.error || !detail.reservation) {
                throw new Error(
                    detail.error ?? 'Não foi possível carregar a reserva.'
                );
            }
            return { match, reservation: detail.reservation };
        },
    });

    const reservation = resolution.data?.reservation;
    const cartPopulatedRef = useRef(false);
    const reservationLinkedRef = useRef(false);

    // Os itens são exibidos (read-only, layout do Checkout) na prévia deslogada
    // e no checkout com reserva válida. Em ambos, populamos o carrinho a partir
    // do link, limpando antes para não herdar itens de uma navegação anterior
    // (o store não persiste, mas vive na mesma sessão SPA). Logado sem reserva
    // válida: o carrinho é limpo para nada vazar para o checkout.
    useEffect(() => {
        if (!paymentLink.data) return;

        const showItems = !token || Boolean(reservation);

        if (!showItems) {
            if (cartPopulatedRef.current) {
                cleanCart();
                cartPopulatedRef.current = false;
                reservationLinkedRef.current = false;
            }
            return;
        }

        if (!cartPopulatedRef.current) {
            cartPopulatedRef.current = true;
            cleanCart();
            setIsPaymentLinkCart(true);

            paymentLink.data.products.forEach(checkoutItem => {
                setCartValues(
                    {
                        id: checkoutItem.product.id.toString(),
                        date: new Date(checkoutItem.date),
                        title: checkoutItem.product.exhibitionName,
                        type: CartServiceType.Waves,
                        value: checkoutItem.value,
                        client: {
                            ...checkoutItem.client,
                            foreigner:
                                paymentLink.data!.person.foreigner || false,
                        },
                    },
                    false
                );
            });
        }

        if (reservation && !reservationLinkedRef.current) {
            reservationLinkedRef.current = true;
            setActiveReservation(reservation);
        }
    }, [
        token,
        reservation,
        paymentLink.data,
        cleanCart,
        setActiveReservation,
        setIsPaymentLinkCart,
        setCartValues,
    ]);

    // Se logado mas sem reserva compatível, redireciona para a Home e exibe Toast
    useEffect(() => {
        if (token && resolution.data && resolution.data.match === null) {
            toast.warn(
                'Não encontramos nenhuma reserva de hospedagem ativa compatível com as datas do agendamento. Verifique se está na conta proprietária da reserva.'
            );
            navigate('/');
        }
    }, [token, resolution.data, navigate]);

    const openLogin = () => setIsVisibleAuthModal(true, 'login');

    const state: PayReservationState = useMemo(() => {
        if (paymentLink.isLoading) return { status: 'loading' };
        if (paymentLink.error) {
            return {
                status: 'error',
                message: getErrorMessage(paymentLink.error.message),
            };
        }
        if (!paymentLink.data) return { status: 'notFound' };

        if (!token) {
            // Prévia: aguarda o carrinho ser populado para renderizar o layout do Checkout.
            if (!cartValues || cartValues.length === 0) {
                return { status: 'loading' };
            }
            return { status: 'preview', forcedCheckoutId: paymentLink.data.id };
        }

        if (resolution.isLoading) return { status: 'loading' };
        if (resolution.isError) {
            return {
                status: 'error',
                message:
                    resolution.error instanceof Error
                        ? resolution.error.message
                        : 'Não foi possível carregar suas reservas.',
            };
        }
        // Aguarda a resolução assentar antes de qualquer decisão: nunca cair no
        // checkout só porque o carrinho tem itens (de uma navegação anterior).
        if (!resolution.data) return { status: 'loading' };
        if (resolution.data.match === null) {
            return { status: 'loading' };
        }
        // Checkout exige reserva válida resolvida E o carrinho já populado pelo link.
        if (!reservation || !cartValues || cartValues.length === 0) {
            return { status: 'loading' };
        }
        return { status: 'checkout', forcedCheckoutId: paymentLink.data.id };
    }, [
        paymentLink.isLoading,
        paymentLink.error,
        paymentLink.data,
        token,
        products,
        resolution.isLoading,
        resolution.isError,
        resolution.error,
        resolution.data,
        reservation,
        cartValues,
    ]);

    return { state, openLogin };
};
